import { useEffect, useState } from "react";
import { Briefcase, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/common/EmptyState";
import { JobCard } from "@/components/jobs/JobCard";
import { QuickApplyDialog } from "@/components/apply/QuickApplyDialog";
import { api } from "@/lib/api";

export default function JobsList() {
  const [jobs, setJobs] = useState([]);
  const [masterData, setMasterData] = useState({ location: [], employment_type: [], hiring_brand: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("all");
  const [employmentType, setEmploymentType] = useState("all");
  const [applyJob, setApplyJob] = useState(null);

  useEffect(() => {
    api.get("/master-data").then((res) => setMasterData(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get("/jobs", { params: { q: q || undefined, location, employment_type: employmentType } })
        .then((res) => setJobs(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, location, employmentType]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight">Find Jobs</h1>
        <p className="text-muted-foreground mt-1">Apply in seconds — all you need is your mobile number.</p>
      </div>

      <div className="bg-white border border-border rounded-xl p-4 mb-6">
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="jobs-filter-search-input" placeholder="Search job title or brand..." className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Select value={location} onValueChange={setLocation}>
            <SelectTrigger data-testid="jobs-filter-location"><SelectValue placeholder="Location" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locations</SelectItem>
              {(masterData.location || []).map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={employmentType} onValueChange={setEmploymentType}>
            <SelectTrigger data-testid="jobs-filter-type"><SelectValue placeholder="Employment type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {(masterData.employment_type || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-56 rounded-xl" />)}
        </div>
      ) : jobs.length === 0 ? (
        <EmptyState icon={Briefcase} title="No jobs match your filters" description="Try adjusting your search or filters." testId="jobs-empty-state" />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="jobs-list">
          {jobs.map((job) => (
            <JobCard key={job.id} job={job} onInterested={() => setApplyJob(job)} />
          ))}
        </div>
      )}

      <QuickApplyDialog job={applyJob} open={!!applyJob} onOpenChange={(v) => !v && setApplyJob(null)} />
    </div>
  );
}
